import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    forwardRef,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormControl,
    FormGroup,
    NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import {
    BehaviorSubject,
    Observable,
    Subject,
    Subscription,
    merge,
} from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
} from 'rxjs/operators';
import { hasValidator } from '../_helpers/functions';
import { AutocompleteV3Service } from './autocomplete-v3.service';

/**
 * Componente de autocomplete que permite selecionar um item de uma lista
 * com suporte a formulários reativos.
 *
 * @example
 * <sume-input-autocomplete-v3
 *   label="Colaborador"
 *   [formGroup]="formGroup"
 *   controlName="colaborador"
 *   [items]="listaDeColaboradores"  // {nome, id_colaborador, outros_dados}[]
 *   itemKeyName="id_colaborador"
 *   itemValueName="nome"
 * >
 * </sume-input-autocomplete-v3>
 *
 *  Com base no exemplo, esse componente vai mostrar uma lista com o
 *  nome e o id de cada colaborador e, quando um for selecionado, o
 *  FormControl será preenchido com o valor do id_colaborador.
 */
@Component({
    selector: 'sume-input-autocomplete-v3',
    templateUrl: './autocomplete-v3.component.html',
    styleUrls: ['./autocomplete-v3.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutocompleteV3Component),
            multi: true,
        },
    ],
})
export class AutocompleteV3Component
    implements OnInit, OnDestroy, ControlValueAccessor
{
    @Input() label: string;
    @Input() formGroup: FormGroup;
    @Input() controlName: string;
    @Input() itemKeyName: string;
    @Input() itemValueName: string;
    @Input() changeText = false;
    @Input() clearOnNotFound = false;
    @Output() current = new EventEmitter<any>();
    @Output() onSelect: EventEmitter<any> = new EventEmitter<any>();

    @ViewChild('instance', { static: true }) instance: NgbTypeahead;

    private readonly _subs = new Subscription();
    private readonly _items$ = new BehaviorSubject<any[]>([]);
    private readonly _destroyed$ = new Subject<void>();

    focus$ = new Subject<string>();
    click$ = new Subject<string>();
    searchControl = new FormControl('');
    isRequired = false;

    private _onChange: any = () => {};
    private _onTouched: any = () => {};

    constructor(private _autocompleteService: AutocompleteV3Service) {}

    @Input()
    get items() {
        return this._items$.value;
    }
    set items(value: any[] | undefined) {
        this._items$.next(value || []);
        if (value?.length && this.formControl?.value) {
            this._syncFormValue();
        }
    }

    get formControl(): AbstractControl {
        return this.formGroup?.get(this.controlName);
    }

    get displayValues(): string[] {
        return this.items?.map(item => item[this.itemValueName]) || [];
    }

    ngOnInit(): void {
        this._validateRequiredFields();
        this._setupSubscriptions();

        // Sincroniza o valor inicial se o formControl já tiver um valor
        if (this.formControl?.value) {
            this._syncFormValue();
        }
    }

    ngOnDestroy(): void {
        this._destroyed$.next();
        this._destroyed$.complete();
        this._subs.unsubscribe();
        this.focus$.complete();
        this.click$.complete();
        this._items$.complete();
    }

    writeValue(value: any): void {
        if (value !== undefined) {
            this.searchControl.setValue(value, { emitEvent: false });
        }
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        isDisabled ? this.searchControl.disable() : this.searchControl.enable();
    }

    search = (text$: Observable<string>) => {
        const debouncedText$ = text$.pipe(
            debounceTime(200),
            distinctUntilChanged()
        );

        const clicksWithClosedPopup$ = this.click$.pipe(
            filter(() => !this.instance?.isPopupOpen())
        );

        return merge(debouncedText$, this.focus$, clicksWithClosedPopup$).pipe(
            map(term =>
                this._autocompleteService.filterItems(this.displayValues, term)
            )
        );
    };

    updateHiddenInput(term: string): void {
        const item = this.items?.find(
            item => item[this.itemValueName] === term
        );

        if (item) {
            this._updateFormValues(item);
        } else {
            this._clearFormValues();
        }

        this._onTouched();
        this.current.emit(this.formControl?.value);
        this.onSelect.emit(this.formControl?.value);
    }

    private _setupSubscriptions(): void {
        // Validação
        if (this.formGroup.controls[this.controlName]?.validator) {
            this.isRequired = hasValidator(
                this.formGroup.controls[this.controlName],
                'required'
            );
        }

        // Estado disabled
        this._subs.add(
            merge(this.formControl?.statusChanges || [], this._items$)
                .pipe(filter(() => !!this.formControl))
                .subscribe(() => {
                    const shouldDisable =
                        this.formControl.disabled || !this.items?.length;
                    shouldDisable
                        ? this.searchControl.disable()
                        : this.searchControl.enable();
                })
        );

        // Valor do campo
        this._subs.add(
            merge(this.formControl?.valueChanges || [], this._items$)
                .pipe(
                    filter(() => !!this.formControl),
                    distinctUntilChanged()
                )
                .subscribe(() => this._syncFormValue())
        );

        // Mudanças no campo de busca
        this._subs.add(
            this.searchControl.valueChanges
                .pipe(distinctUntilChanged())
                .subscribe(value => {
                    if (!value && this.formControl?.value) {
                        this.formControl?.setValue(null, { emitEvent: false });
                        this._onChange(null);
                    }
                    this._onTouched();
                })
        );
    }

    private _syncFormValue(): void {
        const value = this.formControl?.value;
        if (!value || !this.items?.length) {
            this.clearOnNotFound &&
                this.searchControl.setValue('', { emitEvent: false });
            return;
        }

        const item = this.items.find(item => item[this.itemKeyName] === value);
        if (item) {
            this.searchControl.setValue(item[this.itemValueName], {
                emitEvent: false,
            });
        } else if (typeof value === 'string') {
            this.searchControl.setValue(value, { emitEvent: false });
        }

        if (this.searchControl.disabled) {
            setTimeout(() => {
                this.searchControl.setValue(this.searchControl.value, {
                    emitEvent: false,
                });
            });
        }
    }

    private _updateFormValues(item: any): void {
        this.formControl?.setValue(item[this.itemKeyName], {
            emitEvent: false,
        });
        this.searchControl.setValue(item[this.itemValueName], {
            emitEvent: false,
        });
        this._onChange(item[this.itemKeyName]);
    }

    private _clearFormValues(): void {
        this.formControl?.setValue(null, { emitEvent: false });
        if (this.clearOnNotFound) {
            this.searchControl.setValue('', { emitEvent: false });
            this._onChange(null);
        }
    }

    private _validateRequiredFields(): void {
        if (!this.formGroup || !this.formControl || !this.itemValueName) {
            throw new Error(
                'Campos obrigatórios não definidos: formGroup, formControl e itemValueName'
            );
        }
    }
}
